import React, { useState, useEffect } from 'react';
import styles from '../style/ProfilePage.module.css';
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { saveFiles } from '../utils/indexedDB'; 
import { useTheme } from "../context/ThemeContext"; 
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";

// Helper function to convert base64 content to File objects
function base64ToFile(base64String, filename) {
    try {
        const byteString = atob(base64String);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab]);
        return new File([blob], filename);
    } catch (error) {
        console.error("Fehler bei der Konvertierung von Base64 in Datei:", error);
        return null;
    }
}

function CloudSettingsManager({ user }) {
    const { theme } = useTheme();
    const [configs, setConfigs] = useState([]);
    const [provider, setProvider] = useState('S3');
    const [inputs, setInputs] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusMsg, setStatusMsg] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [configToDelete, setConfigToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const apiBaseUrl = `${(process.env.REACT_APP_API_URL || '/api').replace(/\/+$/, '')}`;

    useEffect(() => {
        if (user?.id) {
            setLoading(true);
            fetch(`${apiBaseUrl}/cloud-configs/${user.id}`)
                .then(res => res.json())
                .then(data => { 
                    setConfigs(Array.isArray(data) ? data : []); 
                    setLoading(false); 
                })
                .catch(() => { 
                    setError('Konfigurationen konnten nicht abgerufen werden.'); 
                    setLoading(false); 
                });
        }
    }, [user, apiBaseUrl]);

    const handleInputChange = (e) => {
        const { name, type, value, checked } = e.target;
        setInputs({ ...inputs, [name]: type === 'checkbox' ? checked : value });
    };

    const triggerAutoSync = async () => {
        setStatusMsg('Konfiguration gespeichert. Starte automatische Synchronisierung...');
        try {
            const response = await fetch(`${apiBaseUrl}/sync-from-cloud`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: user.id }),
            });
            
            const result = await response.json();
            
            if (!response.ok) {
                setError(`Konfiguration gespeichert, aber Sync-Fehler: ${result.error || 'Unbekannt'}`);
                return;
            }

            if (result.new_files && result.new_files.length > 0) {
                const filesToSave = result.new_files
                    .map(f => base64ToFile(f.content, f.name))
                    .filter(f => f !== null);

                await saveFiles(filesToSave);
                setStatusMsg(`Erfolg: Konfiguration hinzugefügt und ${filesToSave.length} neue Datei(en) synchronisiert!`);
            } else {
                setStatusMsg('Erfolg: Konfiguration hinzugefügt. Keine neuen Dateien gefunden.');
            }
        } catch (syncErr) {
            console.error("Auto-sync error:", syncErr);
            setError('Konfiguration gespeichert, aber Synchronisierung fehlgeschlagen.');
        }
    };

    const handleAddConfig = async (e) => {
        e.preventDefault();
        setError('');
        setStatusMsg('');
        setLoading(true);
        
        const configTimeout = inputs.timeout ? parseInt(inputs.timeout, 10) : 60;
        let payload;
        
        if (provider === 'S3') {
            payload = { provider: 'S3', config: { bucket_name: inputs.bucket_name, folder_path_prefix: inputs.folder_path_prefix || '', region: inputs.region }, credentials: { access_key_id: inputs.access_key_id, secret_access_key: inputs.secret_access_key } };
        } else if (provider === 'AzureBlob') {
            payload = { provider: 'AzureBlob', config: { container_name: inputs.container_name }, credentials: { connection_string: inputs.connection_string } };
        } else if (provider === 'GCS') {
            try {
                const serviceAccountJson = JSON.parse(inputs.service_account_json);
                payload = { provider: 'GCS', config: { bucket_name: inputs.bucket_name, folder_path_prefix: inputs.folder_path_prefix || '' }, credentials: { service_account_json: serviceAccountJson } };
            } catch (jsonError) {
                setError('Service Account ist kein gültiges JSON.'); 
                setLoading(false);
                return;
            }
        } else if (provider === 'SFTP') {
            payload = { provider: 'SFTP', config: { host: inputs.host, port: inputs.port || 22, remote_path: inputs.remote_path }, credentials: { username: inputs.username, password: inputs.password } };
        } else if (provider === 'FTP') {
            payload = {
                provider: 'FTP',
                config: {
                    host: inputs.host,
                    remote_path: inputs.remote_path,
                    port: inputs.port || 21,
                    passive: inputs.passive !== false, 
                    timeout: configTimeout,
                    use_tls: inputs.use_tls === true,
                },
                credentials: { username: inputs.username, password: inputs.password }
            };
        } else if (provider === 'Local') {
            payload = { provider: 'Local', config: { folder_path: inputs.folder_path }, credentials: {} };
        }

        try {
            const response = await fetch(`${apiBaseUrl}/cloud-configs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...payload, user_id: user.id }),
            });
            
            const result = await response.json();
            
            if (response.ok) {
                setConfigs([...configs, result]);
                setInputs({});
                await triggerAutoSync();
            } else {
                setError(result.error || 'Konfiguration konnte nicht hinzugefügt werden.');
            }
        } catch (err) {
            setError('Netzwerkfehler beim Speichern der Konfiguration.');
        } finally {
            setLoading(false);
        }
    };

    // 1. Triggered when the user clicks the "X" button
    const handleDeleteClick = (configId) => {
        setConfigToDelete(configId);
        setDeleteDialogOpen(true);
    };

    // 2. Triggered when user clicks "Löschen" inside the modal
    const executeDeleteConfig = async () => {
        if (!configToDelete) return;
        setIsDeleting(true);

        try {
            const response = await fetch(`${apiBaseUrl}/cloud-configs/${configToDelete}`, { method: 'DELETE' });
            if (response.ok) {
                setConfigs(configs.filter(c => c.id !== configToDelete));
                setStatusMsg('Konfiguration entfernt.');
            } else {
                const result = await response.json();
                setError(result.error || 'Konfiguration konnte nicht gelöscht werden.');
            }
        } catch (err) {
            setError('Fehler beim Löschen der Konfiguration.');
        } finally {
            setIsDeleting(false);
            setDeleteDialogOpen(false);
            setConfigToDelete(null);
        }
    };

    const isDark = theme === 'dark';
    const dialogPaperStyle = {
        borderRadius: '16px',
        padding: '8px',
        backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
        color: isDark ? '#e0e0e0' : 'inherit',
        border: isDark ? '1px solid #444' : 'none',
    };
    const titleColor = isDark ? '#90caf9' : '#172983';
    const textColor = isDark ? '#b0bec5' : '#334155';
    const cancelBtnColor = isDark ? '#94a3b8' : '#64748b';

    return (
        <div className={styles.cloudSettingsManager}>
            <h2>Cloud-Speicher-Einstellungen</h2>
            
            {loading && <p>Laden...</p>}
            
            {error && <p className={styles.errorText} style={{ color: 'red', marginBottom: '1rem' }}>{error}</p>}
            {statusMsg && <p className={styles.successText} style={{ color: 'green', marginBottom: '1rem', fontWeight: 'bold' }}>{statusMsg}</p>}

            <ul className={styles.configList}>
                {configs.map(c => (
                    <li key={c.id} className={styles.configItem}>
                        <span><strong>{c.provider}:</strong> {c.config.bucket_name || c.config.container_name || c.config.host || c.config.folder_path}</span>
                        <button onClick={() => handleDeleteClick(c.id)} className={styles.deleteButton}>&times;</button>
                    </li>
                ))}
                {configs.length === 0 && !loading && (
                    <p style={{ color: 'var(--text-color)' }}>Keine Cloud-Quellen konfiguriert.</p>
                )}
            </ul>

            <form onSubmit={handleAddConfig} className={styles.addConfigForm}>
                <h3 className={styles.addConfigHeader}>Neue Konfiguration hinzufügen</h3>
                <select value={provider} onChange={(e) => { setProvider(e.target.value); setInputs({}); }} className={styles.providerSelect}>
                    <option value="S3">AWS S3</option>
                    <option value="AzureBlob">Azure Blob Storage</option>
                    <option value="GCS">Google Cloud Storage</option>
                    <option value="SFTP">SFTP Server</option>
                    <option value="FTP">FTP Server (Insecure)</option>
                    <option value="Local">Local Folder</option>
                </select>

                {provider === 'S3' && (
                    <>
                        <input name="bucket_name" value={inputs.bucket_name || ''} onChange={handleInputChange} placeholder="S3 Bucket-Name" required className={styles.configInput} />
                        <input name="region" value={inputs.region || ''} onChange={handleInputChange} placeholder="AWS Region (z.B. us-east-1)" required className={styles.configInput} />
                        <input name="folder_path_prefix" value={inputs.folder_path_prefix || ''} onChange={handleInputChange} placeholder="Ordner-Präfix (optional)" className={styles.configInput} />
                        <input name="access_key_id" value={inputs.access_key_id || ''} onChange={handleInputChange} placeholder="Access Key ID" required className={styles.configInput} />
                        <div className={styles.configInputWrapper}>
                            <input 
                                name="secret_access_key" 
                                type={showPassword ? "text" : "password"} 
                                value={inputs.secret_access_key || ''} 
                                onChange={handleInputChange} 
                                placeholder="Secret Access Key" 
                                required 
                                className={`${styles.configInput} ${styles.passwordConfigInput}`}
                            />
                            <button
                              type="button"
                              className={styles.passwordToggleIcon}
                              onClick={() => setShowPassword(!showPassword)}
                              aria-label={showPassword ? "Passwort ausblenden" : "Passwort anzeigen"}
                            >
                              {showPassword ? <VisibilityOff fontSize="inherit" /> : <Visibility fontSize="inherit" />}
                            </button>
                        </div>
                    </>
                )}

                {provider === 'AzureBlob' && (
                    <>
                        <input name="container_name" value={inputs.container_name || ''} onChange={handleInputChange} placeholder="Azure Container-Name" required className={styles.configInput} />
                        <div className={styles.configInputWrapper}>
                            <input 
                                name="connection_string" 
                                type={showPassword ? "text" : "password"} 
                                value={inputs.connection_string || ''} 
                                onChange={handleInputChange} 
                                placeholder="Azure Connection String" 
                                required 
                                className={`${styles.configInput} ${styles.passwordConfigInput}`} 
                            />
                            <button
                              type="button"
                              className={styles.passwordToggleIcon}
                              onClick={() => setShowPassword(!showPassword)}
                              aria-label={showPassword ? "Passwort ausblenden" : "Passwort anzeigen"}
                            >
                              {showPassword ? <VisibilityOff fontSize="inherit" /> : <Visibility fontSize="inherit" />}
                            </button>
                        </div>
                    </>
                )}

                {provider === 'GCS' && (
                    <>
                        <input name="bucket_name" value={inputs.bucket_name || ''} onChange={handleInputChange} placeholder="GCS Bucket-Name" required className={styles.configInput} />
                        <input name="folder_path_prefix" value={inputs.folder_path_prefix || ''} onChange={handleInputChange} placeholder="Ordner-Präfix (optional)" className={styles.configInput} />
                        <textarea name="service_account_json" value={inputs.service_account_json || ''} onChange={handleInputChange} placeholder="Fügen Sie Ihren Service Account JSON-Schlüssel hier ein" required className={styles.configInput} rows="5" />
                    </>
                )}

                {provider === 'SFTP' && (
                    <>
                        <input name="host" value={inputs.host || ''} onChange={handleInputChange} placeholder="Hostname oder IP-Adresse" required className={styles.configInput} />
                        <input name="port" type="number" value={inputs.port || ''} onChange={handleInputChange} placeholder="Port (Standard: 22)" className={styles.configInput} />
                        <input name="remote_path" value={inputs.remote_path || ''} onChange={handleInputChange} placeholder="Remote-Pfad (z.B. /home/data)" required className={styles.configInput} />
                        <input name="username" value={inputs.username || ''} onChange={handleInputChange} placeholder="Benutzername" required className={styles.configInput} />
                        
                        <div className={styles.configInputWrapper}>
                            <input 
                                name="password" 
                                type={showPassword ? "text" : "password"} 
                                value={inputs.password || ''} 
                                onChange={handleInputChange} 
                                placeholder="Passwort" 
                                required 
                                className={`${styles.configInput} ${styles.passwordConfigInput}`}
                            />
                            <button
                              type="button"
                              className={styles.passwordToggleIcon}
                              onClick={() => setShowPassword(!showPassword)}
                              aria-label={showPassword ? "Passwort ausblenden" : "Passwort anzeigen"}
                            >
                              {showPassword ? <VisibilityOff fontSize="inherit" /> : <Visibility fontSize="inherit" />}
                            </button>
                        </div>
                    </>
                )}
                
                {provider === 'FTP' && (
                    <>
                        <input name="host" value={inputs.host || ''} onChange={handleInputChange} placeholder="Hostname oder IP-Adresse" required className={styles.configInput} />
                        <input name="port" type="number" value={inputs.port || ''} onChange={handleInputChange} placeholder="Port (Standard: 21)" className={styles.configInput} />
                        <input name="remote_path" value={inputs.remote_path || ''} onChange={handleInputChange} placeholder="Remote-Pfad (z.B. /data/uploads)" required className={styles.configInput} />
                        <input name="username" value={inputs.username || ''} onChange={handleInputChange} placeholder="Benutzername" required className={styles.configInput} />
                        
                        <div className={styles.configInputWrapper}>
                            <input 
                                name="password" 
                                type={showPassword ? "text" : "password"} 
                                value={inputs.password || ''} 
                                onChange={handleInputChange} 
                                placeholder="Passwort" 
                                required 
                                className={`${styles.configInput} ${styles.passwordConfigInput}`}
                            />
                            <button
                              type="button"
                              className={styles.passwordToggleIcon}
                              onClick={() => setShowPassword(!showPassword)}
                              aria-label={showPassword ? "Passwort ausblenden" : "Passwort anzeigen"}
                            >
                              {showPassword ? <VisibilityOff fontSize="inherit" /> : <Visibility fontSize="inherit" />}
                            </button>
                        </div>

                        <div className={styles.configInputWrapper}>
                            <label className={styles.formLabel}>
                                <input
                                    type="checkbox"
                                    name="use_tls"
                                    checked={inputs.use_tls === true}
                                    onChange={handleInputChange}
                                />{' '}
                                FTPS (TLS) verwenden
                            </label>
                        </div>
                        <div className={styles.configInputWrapper}>
                            <label className={styles.formLabel}>
                                <input
                                    type="checkbox"
                                    name="passive"
                                    checked={inputs.passive !== false}
                                    onChange={handleInputChange}
                                />{' '}
                                Passivmodus (empfohlen)
                            </label>
                        </div>
                        <input
                            name="timeout"
                            type="number"
                            value={inputs.timeout || ''}
                            onChange={handleInputChange}
                            placeholder="Timeout in Sekunden (z.B. 30)"
                            className={styles.configInput}
                        />
                    </>
                )}
                
                {provider === 'Local' && (
                    <input name="folder_path" value={inputs.folder_path || ''} onChange={handleInputChange} placeholder="Vollständiger Ordnerpfad (z.B. C:\Data)" required className={styles.configInput} />
                )}
                
                <button type="submit" className={styles.addButton}>Konfiguration hinzufügen</button>
            </form>

            {/* FIX #2: Custom Delete Confirmation Modal */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                PaperProps={{
                  style: dialogPaperStyle
                }}
            >
                <DialogTitle id="delete-dialog-title" sx={{ fontWeight: 700, color: titleColor }}>
                    Konfiguration löschen?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description" sx={{ color: textColor }}>
                        Sind Sie sicher, dass Sie diese Cloud-Speicher-Konfiguration löschen möchten?
                        <br /><br />
                        Die Verbindung wird getrennt, aber bereits synchronisierte Dateien bleiben erhalten.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button 
                        onClick={() => setDeleteDialogOpen(false)} 
                        sx={{ 
                          color: cancelBtnColor, 
                          fontWeight: 600,
                          textTransform: 'none'
                        }}
                    >
                        Abbrechen
                    </Button>
                    <Button 
                        onClick={executeDeleteConfig} 
                        disabled={isDeleting}
                        variant="contained"
                        sx={{
                          bgcolor: '#d32f2f',
                          '&:hover': { bgcolor: '#b71c1c' },
                          borderRadius: '8px',
                          fontWeight: 700,
                          textTransform: 'none',
                          boxShadow: '0 4px 12px rgba(211, 47, 47, 0.2)'
                        }}
                        autoFocus
                    >
                        {isDeleting ? "Wird gelöscht..." : "Löschen"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default CloudSettingsManager;